// EL DELTA EN PALABRAS (Process-Compiled Agent, Wave 2 — W2-4).
//
// Dos compilaciones del mismo Agente, dicha la diferencia en el vocabulario del
// CLIENTE. Un Dueño no lee nodos, aristas ni `extract`: lee opciones de su menú
// y preguntas de sus procesos, que son las palabras que él mismo escribió en
// Procesos y en Datos del proceso. Esa traducción es todo este archivo.
//
// POR QUÉ EXISTE. La ola 2 es SOLO vista previa: nada se publica y nada se
// escribe. Lo único que la hace útil es poder poner lado a lado lo que el agente
// hace HOY y lo que haría si se compilara desde los procesos, y que el cliente
// lo entienda sin abrir el editor. CP-2 es exactamente esa comparación.
//
// LA VOZ NO SE INVENTA AQUÍ. Es la de los diálogos de flujo de captura
// (packages/api/src/skills/capture-flow-merge.ts, `captureFlowDeltaLines`):
// impersonal, sujeto = lo que se hace y no quien lo hace ("Se agregará...",
// "Se quitará...", "Se conservará..."), enumeración en español sin coma de
// Oxford, y el hecho entre comillas. Los fragmentos de abajo REUSAN esas formas
// a propósito: dos pantallas del mismo producto que describen el mismo tipo de
// cambio con dos registros distintos se leen como dos productos.
//
// GRANULARIDAD v1: proceso (opción agregada / quitada / renombrada / reordenada),
// pregunta dentro de la captura de un proceso (agregada / quitada / reordenada /
// tipo cambiado), las opciones BASE del menú (las que no salen de un proceso, y
// las que el menú vivo trae sin que nada las respalde — F2-1), el saludo y las
// terminales. NUNCA nodos: el cliente no los ve, no los nombró y no puede
// actuar sobre ellos.
//
// DETERMINISTA. Mismo par de entradas, mismas frases en el mismo orden, siempre.
// El compilador se rompió la espalda para ser byte-idéntico (ver la cabecera de
// `compile.ts`) justamente para que este archivo pueda decir "no cambió nada" y
// que sea verdad.
//
// PURO. Sin I/O, sin `Date`, sin aleatoriedad, y sin más importaciones que
// TIPOS y constantes puras del propio compilador: es la regla del módulo y es
// lo que lo deja correr igual en el servidor y en el navegador.

import { CAPTURE_CATEGORY_KEY } from './capture-flow.js'
import type { CompilerCategory } from './types.js'
import type { FichaDataType } from '../bot-config/field-kind-map.js'
import type { AskExtract } from '../skills/types.js'

/** Cómo se nombra el menú del Descubrir cuando se le habla al cliente. UNA sola
 *  fuente: `packages/api/src/skills/capture-flow-generator.ts` la re-exporta
 *  desde aquí en vez de llevar su propia copia, porque las frases de la captura
 *  y las de esta vista previa se leen juntas en la misma pantalla. */
export const AGENT_MENU_TERM = 'menú del agente'

/** CÓMO SE LLAMA EL CONTROL donde el cliente escribe la entrada de un proceso
 *  (F4-5), dicho tal cual aparece en Configuración → Procesos.
 *
 *  UNA sola fuente, por la misma razón que la de arriba y con más filo: desde
 *  F4-8 el recibo de la captura NOMBRA este control ("la editas en …") para que
 *  la etiqueta de entrada deje de leerse como una decisión que alguien más tomó.
 *  Una copia del literal en el servidor, otra en la pantalla y otra en el
 *  rechazo del PATCH significan que el día que el control se llame distinto,
 *  dos de las tres mandarían al cliente a buscar algo que ya no existe. */
export const ENTRY_LABEL_CONTROL_TERM = 'Cómo lo reconoce el agente'

/** CÓMO SE LLAMA EL CONTROL donde el cliente escribe el SIGNIFICADO de un
 *  proceso (F5-2), tal cual aparece en Configuración → Procesos, junto al de
 *  arriba. Vive aquí por la misma razón: la pantalla lo pinta y el rechazo del
 *  PATCH lo nombra, y dos copias del literal se separan el día que el control
 *  cambie de nombre. */
export const MEANING_CONTROL_TERM = 'Qué significa este proceso'

/** A dónde termina la rama de un proceso. Los mismos tres destinos que
 *  `processTerminal` de `compile.ts` deriva de los behaviors (D1/D2). */
export type AgentTerminal = 'pasar' | 'cierre' | 'fin_ok'

/** Una pregunta de la captura de un proceso, como el cliente la ve. */
export interface AgentQuestionShape {
  /** La clave del dato. Es la identidad: renombrar la etiqueta no la cambia. */
  key: string
  /** El nombre que el cliente le puso al dato en Datos del proceso. */
  label: string
  /** Cómo se lee la respuesta, ya normalizado a un token comparable
   *  (`extractToken`). String opaco a propósito: este archivo COMPARA, no
   *  interpreta, y así el proyector del lado vivo y el del lado compilado
   *  producen la misma moneda. */
  kind: string
  /** El tipo de la ficha, cuando se conoce. No se usa para comparar (`kind` ya
   *  lo hace); viaja para que la pantalla pueda etiquetarlo con la misma tabla
   *  que ya usa Datos del proceso (`DATA_TYPE_LABELS`), sin una segunda tabla. */
  dataType?: FichaDataType | null
}

/** Un proceso, como el cliente lo ve: una opción del menú y sus preguntas. */
export interface AgentProcessShape {
  /** La clave del proceso = la clave de la opción del menú. La identidad con la
   *  que se emparejan los dos lados. */
  key: string
  /** El nombre del proceso, tal como el cliente lo escribió. */
  name: string
  /** La etiqueta que la opción lleva en el menú (una frase, no el nombre). */
  label: string
  questions: AgentQuestionShape[]
  terminal: AgentTerminal
  /** ¿Este proceso AGENDA? (D22-7). El lado compilado lo lee de su plan (un
   *  tramo de `agenda`) y el lado vivo de lo que el flujo llamado CORRE (un
   *  `act` que reserva), que son las dos maneras de mirar el mismo hecho.
   *
   *  Viaja porque el menú lo necesita dicho: la opción de cita de FÁBRICA se
   *  retira cuando un proceso agenda, y la frase que lo cuenta NOMBRA al proceso
   *  que la sustituye. Opcional para no obligar a cada llamador viejo a
   *  declararlo: sin dato, no se nombra sucesor y la frase cae en la genérica. */
  books?: boolean
}

/** De dónde sale una opción BASE del menú, dicho en palabras del cliente.
 *
 *  Conjunto CERRADO, y se deriva de la ACCIÓN de la categoría
 *  (`CompilerCategory.action`), no de su clave: la clave es plomería por giro
 *  (`comprar`, `avaluo`, `cita`) y no dice nada del papel que juega la opción.
 *  La única excepción es `handoff`, donde la acción es LITERALMENTE la misma
 *  para "describe una urgencia" y para "pide hablar con una persona" — ahí el
 *  único hecho que las separa ES la clave, y por eso ahí sí se mira. */
export type AgentCategorySource =
  | 'agenda'
  | 'catalogo'
  | 'conocimiento'
  | 'urgencia'
  | 'persona'
  | 'despedida'
  /** El lado VIVO tiene una opción que la compilación no emite: no se sabe de
   *  dónde vino (la escribió alguien a mano en el editor). */
  | 'otra'

/** Una opción BASE del menú: las que NO salen de un proceso del espacio. */
export interface AgentCategoryShape {
  /** La clave de la opción del `route`. Es la identidad con la que se emparejan
   *  los dos lados. */
  key: string
  /** La frase que el menú lleva escrita. */
  label: string
  source: AgentCategorySource
}

/**
 * De qué habla una categoría, leído de su acción.
 *
 * `schedule` / `manage` / `book` agendan o piden una visita; `quote` cotiza del
 * catálogo; `answer` contesta de la base de conocimiento; `end` se despide.
 * `capture` y `call` guardan un prospecto y no tienen un papel base propio: son
 * la captura de un proceso con un disfraz más viejo, así que no se les inventa
 * una casa y se leen como `otra`.
 */
export function categorySourceOf(category: CompilerCategory): AgentCategorySource {
  const action = category.action
  switch (action.kind) {
    case 'schedule':
    case 'manage':
    case 'book':
      return 'agenda'
    case 'quote':
      return 'catalogo'
    case 'answer':
      return 'conocimiento'
    case 'end':
      return 'despedida'
    case 'handoff':
      // Ver `AgentCategorySource`: las dos ramas de traspaso comparten acción y
      // lo único que las distingue es la clave.
      return category.key === 'urgencia' ? 'urgencia' : 'persona'
    default:
      return 'otra'
  }
}

/** El Agente entero, en vocabulario de cliente. Es la forma que se compara: el
 *  lado COMPILADO sale de la compilación, y el lado VIVO lo arma la ruta leyendo
 *  los flujos publicados. Dos orígenes, una sola forma. */
export interface AgentShape {
  greeting: string
  /** D6: el consentimiento es una etapa VIRTUAL. Se dibuja, no se compila. */
  consent: boolean
  processes: AgentProcessShape[]
  /** Las opciones BASE del menú, en el orden en que el menú las lleva. Están
   *  aquí porque el menú es UNO (D15): comparar sólo los procesos hacía que el
   *  delta callara siete ramas del agente vivo, y un `Actualizar` las habría
   *  borrado sin decirlo (F2-1). */
  categories: AgentCategoryShape[]
}

/** Lo que cambia en UN proceso. La pantalla agrupa por esto. */
export interface AgentProcessDelta {
  key: string
  /** El nombre con el que se le nombra en las frases: el del lado nuevo cuando
   *  sigue, el del viejo cuando se quita. */
  name: string
  status: 'added' | 'removed' | 'kept'
  /** La opción se renombró (el proceso cambió de nombre, o la conexión le puso
   *  una etiqueta propia). */
  labelChanged: { from: string; to: string } | null
  /** Cambió de lugar en el menú. Posiciones base 1, como se cuentan en voz alta. */
  positionChanged: { from: number; to: number } | null
  /** Etiquetas de los datos cuya pregunta entra / sale. */
  questionsAdded: string[]
  questionsRemoved: string[]
  /** Las mismas preguntas, en otro orden. */
  questionsReordered: boolean
  /** Datos que siguen, pero cuya respuesta se leerá distinto. */
  questionKindChanged: string[]
  terminalChanged: { from: AgentTerminal; to: AgentTerminal } | null
  /** EL PROCESO EMPEZÓ — O DEJÓ — DE AGENDAR (D22-8).
   *
   *  Es el cambio más grande que un proceso puede tener y hasta hoy no se decía:
   *  con las MISMAS preguntas y la MISMA terminal, prender la columna que agenda
   *  no producía ni una frase, así que la vista previa contestaba "el agente ya
   *  refleja tus procesos" y el Publicar se refusaba por `nothing_to_publish`
   *  sobre un agente que sí iba a cambiar de comportamiento. Sin dato en alguno
   *  de los dos lados se lee como "no agenda", que es lo que un llamador viejo
   *  quiso decir al no declararlo. */
  booksChanged: boolean
  /** Las frases de ESTE proceso, ya redactadas. La pantalla no arma frases: si
   *  las armara, la agrupación y el resumen podrían contar cosas distintas. */
  sentences: string[]
}

export interface AgentDelta {
  greetingChanged: boolean
  /** Cambió el orden del menú sin que entrara ni saliera ninguna opción. */
  menuReordered: boolean
  /** D6: estructuralmente SIEMPRE `null`. La puerta del consentimiento corre
   *  siempre y no se puede apagar, así que los dos lados traen `consent: true`
   *  y no hay nada que comparar. Lo que sí varía (modo de aceptación, versión
   *  de los términos, aviso propio) se ENSEÑA en la vista previa, no se
   *  compara: no lo escribe una compilación, lo escribe el cliente en
   *  Configuración → Privacidad, así que un `Actualizar` nunca lo tocaría. El
   *  campo se queda porque la comparación sí es correcta el día que exista un
   *  espacio sin puerta. */
  consentChanged: { from: boolean; to: boolean } | null
  /** Un elemento por proceso TOCADO, en orden del menú nuevo y después los que
   *  se quitan. Los procesos sin cambios no aparecen: una lista donde todo sale
   *  esconde lo que sí cambió. */
  processes: AgentProcessDelta[]
  /** Etiquetas de las opciones BASE que entran (están compiladas y no vivas). */
  categoriesAdded: string[]
  /** Etiquetas de las opciones BASE que salen: vivas, no compiladas, y con un
   *  papel que sí se sabe nombrar. */
  categoriesRemoved: string[]
  /** Etiquetas de las opciones del menú VIVO que no vienen de ningún proceso ni
   *  de ninguna opción base compilada: exactamente lo que un `Actualizar`
   *  borraría. Alguien las escribió a mano en el editor y el compilador no
   *  tiene de dónde volver a emitirlas. */
  unaccountedLiveOptions: string[]
  /** LAS MISMAS opciones, por CLAVE y en el MISMO orden. La lista de arriba es
   *  para LEER — son etiquetas, y una etiqueta la reescribe el cliente cuando
   *  quiere — y ésta es para DECIDIR: el gate del Publicar tiene que poder
   *  eximir una opción conocida (la captura de fábrica, `CAPTURE_CATEGORY_KEY`)
   *  sin adivinarla desde su etiqueta. Dos arreglos paralelos y no una lista de
   *  pares porque la pantalla ya consume `unaccountedLiveOptions` tal cual, y
   *  cambiarle la forma sería romper un contrato vivo para agregar un campo. */
  unaccountedLiveOptionKeys: string[]
  /** Todas las frases, en orden determinista. Es lo que la pantalla lista bajo
   *  "Qué cambiaría". */
  summary: string[]
  /** No hay nada que decir. Es un resultado, no un error. */
  isEmpty: boolean
}

/** La frase de la pantalla cuando no hay delta. El silencio decía dos cosas a la
 *  vez ("ya está al día" y "esto todavía no compara nada"), y sólo una era
 *  cierta: la misma disciplina que `CAPTURE_FLOW_SYNCED_LINE`. */
export const AGENT_PREVIEW_NO_DELTA = 'El agente ya refleja tus procesos.'

/**
 * La moneda de comparación de "cómo se lee la respuesta".
 *
 * Un token canónico en vez de comparar dos `AskExtract` estructura contra
 * estructura, porque los dos lados llegan por caminos distintos (el compilado
 * baja de la ficha con `askExtractForDataType`; el vivo se lee del flujo
 * publicado) y necesitan una sola definición de "es la misma lectura". Las
 * opciones del `enum` van DENTRO del token: una lista cuyas opciones cambiaron
 * es otra lectura, y ésa es justo la deriva de F39.
 *
 * D21-3 le pone UNA excepción, escrita abajo: la lista que no escribió el
 * cliente se compara por su FUENTE.
 */
export function extractToken(extract: AskExtract): string {
  if (extract.kind === 'enum') {
    // …SALVO CUANDO LA LISTA NO ES DEL CLIENTE (D21-3). Un dato con fuente se
    // compara por su FUENTE y nunca por sus opciones: las opciones son de la
    // Agenda o del Catálogo y se mueven solas, así que meterlas en el token
    // haría que dar de alta un médico pintara en rojo un proceso que nadie
    // tocó, y que el "Qué cambiaría" del Publicar enumerara una pregunta que
    // sigue diciendo exactamente lo mismo. Cambiar de fuente, ponerle una o
    // quitársela SÍ es otra lectura, y por eso va dentro del token.
    return extract.source !== undefined
      ? `enum:src:${extract.source}`
      : `enum:${extract.values.join('|')}`
  }
  if (extract.kind === 'regex') return `regex:${extract.pattern}`
  return extract.kind
}

/** Una enumeración en español: "a", "a y b", "a, b y c". Sin coma de Oxford,
 *  que en español no se usa. Gemela de las de `capture-flow-generator.ts` y
 *  `capture-flow-merge.ts`; vive aquí porque `@vysto/shared` no puede importar
 *  `@vysto/api` y este módulo es puro. */
function joinEs(items: readonly string[]): string {
  if (items.length <= 1) return items[0] ?? ''
  return `${items.slice(0, -1).join(', ')} y ${items[items.length - 1]!}`
}

function quoted(items: readonly string[]): string {
  return joinEs(items.map((i) => `"${i}"`))
}

/** Cómo se dice en voz alta a dónde va a parar una conversación. El cliente
 *  nunca vio los ids `pasar` / `cierre` / `fin_ok`, así que la frase dice lo que
 *  PASA, no cómo se llama el nodo. Se completa con "terminará ___". */
const TERMINAL_PHRASE: Record<AgentTerminal, string> = {
  pasar: 'con una persona',
  cierre: 'con una despedida',
  fin_ok: 'sin pasar a una persona',
}

function sameOrder(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((k, i) => k === b[i])
}

/** La etiqueta reducida a lo que el cliente considera "la misma palabra": sin
 *  espacios de sobra, sin mayúsculas y sin acentos. No se guarda en ningún lado
 *  ni se muestra: sólo se compara. */
function normalizedLabel(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
}

/** Una pregunta que está en los DOS lados, ya emparejada. */
interface QuestionPair {
  before: AgentQuestionShape
  beforeIndex: number
  after: AgentQuestionShape
  afterIndex: number
}

/**
 * Qué pregunta de un lado es cuál del otro.
 *
 * DOS PASADAS, y la segunda es el arreglo de D4. Primero por CLAVE, que es la
 * identidad real: renombrar la etiqueta no cambia el dato. Después, sobre lo que
 * quedó suelto en los dos lados, por ETIQUETA NORMALIZADA.
 *
 * POR QUÉ. Una misma pregunta puede llevar dos claves distintas debajo, porque
 * hay dos convenciones para el mismo papel: el flujo de captura de FÁBRICA
 * pregunta la especialidad en la variable `especialidad`, y la ficha del espacio
 * guarda ese dato en el preset administrado `interes` — las dos etiquetadas
 * "Especialidad". Emparejando sólo por clave, el delta decía en dos renglones
 * seguidos que se agrega la pregunta de "Especialidad" y que se quita la
 * pregunta de "especialidad": para un Dueño eso no es un cambio, es un galimatías.
 * El cliente ve ETIQUETAS; las claves son plomería, y un cambio de clave no se
 * puede leer como quitar y agregar la misma pregunta.
 *
 * Lo que la etiqueta NO tapa: el `kind`. Un par emparejado por etiqueta sigue
 * comparando cómo se lee la respuesta, así que un cambio de tipo bajo la misma
 * etiqueta se sigue diciendo.
 *
 * DETERMINISTA con etiquetas repetidas: se recorre el lado nuevo en su orden y
 * cada una toma la PRIMERA candidata libre del lado viejo, también en su orden.
 * Las que sobran quedan como agregadas / quitadas, que es la verdad.
 */
function pairQuestions(
  before: readonly AgentQuestionShape[],
  after: readonly AgentQuestionShape[],
): { pairs: QuestionPair[]; addedIndexes: number[]; removedIndexes: number[] } {
  const pairs: QuestionPair[] = []
  const pairedBefore = new Set<number>()
  const pairedAfter = new Set<number>()

  const pair = (i: number, j: number): void => {
    pairs.push({ before: before[i]!, beforeIndex: i, after: after[j]!, afterIndex: j })
    pairedBefore.add(i)
    pairedAfter.add(j)
  }

  after.forEach((q, j) => {
    const i = before.findIndex((b, k) => !pairedBefore.has(k) && b.key === q.key)
    if (i !== -1) pair(i, j)
  })
  after.forEach((q, j) => {
    if (pairedAfter.has(j)) return
    const label = normalizedLabel(q.label)
    const i = before.findIndex((b, k) => !pairedBefore.has(k) && normalizedLabel(b.label) === label)
    if (i !== -1) pair(i, j)
  })

  pairs.sort((a, b) => a.afterIndex - b.afterIndex)
  return {
    pairs,
    addedIndexes: after.map((_, j) => j).filter((j) => !pairedAfter.has(j)),
    removedIndexes: before.map((_, i) => i).filter((i) => !pairedBefore.has(i)),
  }
}

/** El delta de UN proceso que está en los dos lados. `null` cuando no cambió
 *  nada: un proceso intacto no es una fila vacía en la lista, es una ausencia. */
function diffKeptProcess(
  before: AgentProcessShape,
  after: AgentProcessShape,
  beforePos: number,
  afterPos: number,
  future: boolean,
): AgentProcessDelta | null {
  const v = (f: string, p: string): string => (future ? f : p)
  const { pairs, addedIndexes, removedIndexes } = pairQuestions(before.questions, after.questions)

  // El orden es el del lado NUEVO, que es el de la ficha: un dato que entró se
  // nombra donde el cliente lo puso, no siempre al final.
  const questionsAdded = addedIndexes.map((j) => after.questions[j]!.label)
  const questionsRemoved = removedIndexes.map((i) => before.questions[i]!.label)
  // La etiqueta con la que se NOMBRA un cambio de lectura es la del lado nuevo:
  // es la que el cliente va a ver en la pantalla después de aplicar.
  const questionKindChanged = pairs
    .filter((p) => p.before.kind !== p.after.kind)
    .map((p) => p.after.label)

  // Reordenar es SOLO reordenar: si además entró o salió una pregunta, el orden
  // cambió por consecuencia y decirlo aparte sería contar dos veces el mismo
  // hecho. Se comparan los PARES: en el orden del lado nuevo, los del lado viejo
  // tienen que ir de menos a más.
  const survivorsAfter = pairs.map((p) => String(p.beforeIndex))
  const survivorsBefore = [...pairs]
    .sort((a, b) => a.beforeIndex - b.beforeIndex)
    .map((p) => String(p.beforeIndex))
  const questionsReordered = !sameOrder(survivorsBefore, survivorsAfter)

  const labelChanged = before.label !== after.label ? { from: before.label, to: after.label } : null
  const positionChanged = beforePos !== afterPos ? { from: beforePos, to: afterPos } : null
  const terminalChanged =
    before.terminal !== after.terminal ? { from: before.terminal, to: after.terminal } : null
  // D22-8: sin dato = no agenda. Los dos lados se normalizan ANTES de comparar
  // para que `undefined` contra `false` no invente un cambio que nadie hizo.
  const booksChanged = (before.books === true) !== (after.books === true)

  const touched =
    questionsAdded.length > 0 ||
    questionsRemoved.length > 0 ||
    questionKindChanged.length > 0 ||
    questionsReordered ||
    labelChanged !== null ||
    positionChanged !== null ||
    terminalChanged !== null ||
    booksChanged
  if (!touched) return null

  const name = after.name
  const sentences: string[] = []
  if (labelChanged) {
    sentences.push(
      `La opción del ${AGENT_MENU_TERM} de "${name}" ${v('pasará', 'pasó')} a decir ` +
        `"${labelChanged.to}".`,
    )
  }
  if (positionChanged) {
    sentences.push(
      `"${name}" ${v('pasará', 'pasó')} al lugar ${positionChanged.to} del ${AGENT_MENU_TERM}.`,
    )
  }
  if (questionsAdded.length > 0) {
    sentences.push(
      questionsAdded.length === 1
        ? `En "${name}" ${v('se agregará', 'se agregó')} la pregunta de ${quoted(questionsAdded)}.`
        : `En "${name}" ${v('se agregarán', 'se agregaron')} las preguntas de ` +
            `${quoted(questionsAdded)}.`,
    )
  }
  if (questionsRemoved.length > 0) {
    sentences.push(
      questionsRemoved.length === 1
        ? `En "${name}" ${v('se quitará', 'se quitó')} la pregunta de ${quoted(questionsRemoved)}.`
        : `En "${name}" ${v('se quitarán', 'se quitaron')} las preguntas de ` +
            `${quoted(questionsRemoved)}.`,
    )
  }
  if (questionsReordered) {
    sentences.push(`En "${name}" ${v('cambiará', 'cambió')} el orden de las preguntas.`)
  }
  if (questionKindChanged.length > 0) {
    sentences.push(
      `En "${name}" ${v('se actualizará', 'se actualizó')} cómo se lee la respuesta de ` +
        `${quoted(questionKindChanged)}.`,
    )
  }
  // QUÉ HACE AHORA EL PROCESO, antes de decir cómo termina: la reserva ocurre
  // dentro de la conversación y la terminal es lo que pasa después. La frase
  // dice las TRES cosas que cambian de verdad (busca, reserva, mueve), porque
  // "ahora agenda" a secas no distingue prender la columna de haberle puesto un
  // nombre nuevo.
  if (booksChanged) {
    sentences.push(
      after.books === true
        ? `"${name}" ${v('pasará', 'pasó')} a agendar la cita: busca el horario real, la ` +
            'reserva y mueve la tarjeta.'
        : `"${name}" ${v('dejará', 'dejó')} de agendar la cita: el agente captura los datos y ` +
            'una persona da seguimiento.',
    )
  }
  if (terminalChanged) {
    sentences.push(
      `En "${name}" la conversación ${v('pasará', 'pasó')} a terminar ` +
        `${TERMINAL_PHRASE[terminalChanged.to]}.`,
    )
  }

  return {
    key: after.key,
    name,
    status: 'kept',
    labelChanged,
    positionChanged,
    questionsAdded,
    questionsRemoved,
    questionsReordered,
    questionKindChanged,
    terminalChanged,
    booksChanged,
    sentences,
  }
}

/**
 * El delta completo, en frases que un Dueño lee.
 *
 * `future` la escribe en futuro (la vista previa PROMETE) y en pasado el recibo
 * confirma. Misma disciplina que `captureFlowDeltaLines`: una sola cuenta
 * detrás de las dos frases, para que el recibo no pueda describir un cambio
 * distinto del que el cliente aceptó.
 * TODO(Wave 4 — aplicar): el recibo en pasado es de esa ola. La ola 2 sólo
 * muestra; nada publica todavía.
 */
export function diffAgents(before: AgentShape, after: AgentShape, future = true): AgentDelta {
  const v = (f: string, p: string): string => (future ? f : p)
  const beforeByKey = new Map(before.processes.map((p, i) => [p.key, { p, i }]))
  const afterByKey = new Map(after.processes.map((p, i) => [p.key, { p, i }]))

  const processes: AgentProcessDelta[] = []
  const menuSentences: string[] = []

  // Los procesos, en el orden del menú NUEVO: es el orden en el que el cliente
  // los va a ver en la pantalla, así que es el orden en el que se le cuentan.
  for (const [i, p] of after.processes.entries()) {
    const old = beforeByKey.get(p.key)
    if (!old) {
      processes.push({
        key: p.key,
        name: p.name,
        status: 'added',
        labelChanged: null,
        positionChanged: null,
        questionsAdded: p.questions.map((q) => q.label),
        questionsRemoved: [],
        questionsReordered: false,
        questionKindChanged: [],
        terminalChanged: null,
        // D22-8: un proceso que ENTRA agendando no "cambió" de comportamiento,
        // pero sí lo tiene, y el renglón de sus preguntas no lo dice.
        booksChanged: p.books === true,
        sentences: [
          `${v('Se agregará', 'Se agregó')} al ${AGENT_MENU_TERM} la opción "${p.label}".`,
          ...(p.questions.length > 0
            ? [
                `En "${p.name}" ${v('se preguntará', 'se preguntó')} ` +
                  `${joinEs(p.questions.map((q) => q.label))}.`,
              ]
            : []),
          ...(p.books === true
            ? [
                `"${p.name}" ${v('agendará', 'agenda')} la cita: busca el horario real, la ` +
                  'reserva y mueve la tarjeta.',
              ]
            : []),
          `En "${p.name}" la conversación ${v('terminará', 'terminó')} ` +
            `${TERMINAL_PHRASE[p.terminal]}.`,
        ],
      })
      continue
    }
    const d = diffKeptProcess(old.p, p, old.i + 1, i + 1, future)
    if (d) processes.push(d)
  }

  // Los que SALEN, después y en el orden que tenían: se leen como el final de la
  // lista porque son lo que deja de existir.
  for (const p of before.processes) {
    if (afterByKey.has(p.key)) continue
    processes.push({
      key: p.key,
      name: p.name,
      status: 'removed',
      labelChanged: null,
      positionChanged: null,
      questionsAdded: [],
      questionsRemoved: p.questions.map((q) => q.label),
      questionsReordered: false,
      questionKindChanged: [],
      terminalChanged: null,
      // Un proceso que SALE del menú se lleva todo lo suyo, la agenda incluida:
      // la opción que desaparece ya lo dice entero y un segundo renglón sobre
      // una reserva que nadie va a poder pedir sería ruido.
      booksChanged: false,
      sentences: [`${v('Se quitará', 'Se quitó')} del ${AGENT_MENU_TERM} la opción "${p.label}".`],
    })
  }

  // ¿El menú se reordenó SIN que entrara ni saliera nada? Se dice una vez arriba
  // en vez de repetir "pasará al lugar N" en cada proceso movido: mover uno
  // mueve a todos los de abajo, y esa cascada leída proceso por proceso suena a
  // que cambió mucho más de lo que cambió.
  const beforeKeys = before.processes.map((p) => p.key)
  const afterKeys = after.processes.map((p) => p.key)
  const sameSet =
    beforeKeys.length === afterKeys.length && beforeKeys.every((k) => afterByKey.has(k))
  const menuReordered = sameSet && !sameOrder(beforeKeys, afterKeys)
  if (menuReordered) {
    menuSentences.push(
      `${v('Cambiará', 'Cambió')} el orden de las opciones del ${AGENT_MENU_TERM}.`,
    )
    // Con la frase de arriba puesta, la de cada proceso movido sobra.
    for (const d of processes) {
      if (d.positionChanged === null) continue
      d.sentences = d.sentences.filter((s) => !s.endsWith(`del ${AGENT_MENU_TERM}.`))
    }
  }

  const greetingChanged = before.greeting !== after.greeting
  const consentChanged =
    before.consent !== after.consent ? { from: before.consent, to: after.consent } : null

  const summary: string[] = []
  if (greetingChanged) {
    summary.push(`${v('Cambiará', 'Cambió')} el saludo con el que el agente abre la conversación.`)
  }
  if (consentChanged) {
    summary.push(
      consentChanged.to
        ? `${v('Se pedirá', 'Se pidió')} el consentimiento antes de cualquier proceso.`
        : `${v('Se dejará', 'Se dejó')} de pedir el consentimiento antes de los procesos.`,
    )
  }
  summary.push(...menuSentences)
  for (const d of processes) summary.push(...d.sentences)

  // LAS OPCIONES BASE. Se cuentan después de los procesos porque son el fondo
  // del menú y no lo que el cliente vino a configurar, pero se cuentan: son
  // ramas que el agente ofrece hoy.
  const beforeCatsByKey = new Map(before.categories.map((c) => [c.key, c]))
  const afterCatKeys = new Set(after.categories.map((c) => c.key))
  const categoriesAdded = after.categories
    .filter((c) => !beforeCatsByKey.has(c.key))
    .map((c) => c.label)
  // Vivas y no compiladas. `otra` = nadie sabe de dónde salió (la escribieron a
  // mano); cualquier otro origen = una opción base que la compilación dejó de
  // emitir. Son frases distintas porque son hechos distintos, y por eso las dos
  // listas son DISJUNTAS: una misma opción nunca se cuenta dos veces.
  const liveOnly = before.categories.filter((c) => !afterCatKeys.has(c.key))
  const removedCats = liveOnly.filter((c) => c.source !== 'otra')
  const categoriesRemoved = removedCats.map((c) => c.label)
  // UNA sola lista filtrada, leída de dos maneras: la etiqueta para las frases y
  // la clave para el gate. Filtrar dos veces sería la puerta abierta a que las
  // dos dejen de estar alineadas índice por índice.
  const unaccounted = liveOnly.filter((c) => c.source === 'otra')
  const unaccountedLiveOptions = unaccounted.map((c) => c.label)
  const unaccountedLiveOptionKeys = unaccounted.map((c) => c.key)

  for (const label of categoriesAdded) {
    summary.push(`${v('Se agregará', 'Se agregó')} al menú la opción base "${label}".`)
  }
  // QUIÉN SE QUEDA CON LA AGENDA (D22-7). La opción base de cita se retira
  // porque un proceso conectado es el que agenda ahora, y decirlo sin nombrarlo
  // deja al cliente creyendo que su agente dejó de poder dar citas. El sucesor
  // es el primer proceso del lado NUEVO que agenda, en el orden del menú.
  const bookingSuccessor = after.processes.find((p) => p.books === true) ?? null
  for (const c of removedCats) {
    summary.push(
      c.source === 'agenda' && bookingSuccessor !== null
        ? `${v('Se quitará', 'Se quitó')} del menú la opción base "${c.label}": ahora la agenda ` +
            `tu proceso "${bookingSuccessor.name}".`
        : `${v('Se quitará', 'Se quitó')} del menú la opción base "${c.label}".`,
    )
  }
  for (const c of unaccounted) {
    // LA CAPTURA DE FÁBRICA NO ES UNA OPCIÓN DE NADIE, y su frase lo dice. El
    // gate del Publicar la exime (`agent-publish.ts`), así que la frase genérica
    // de procedencia era la ÚNICA oración del primer delta sobre la que el
    // cliente no podía actuar: la opción sale del menú porque las de sus
    // procesos la sustituyen, y eso es lo que hay que decir.
    summary.push(
      c.key === CAPTURE_CATEGORY_KEY
        ? `${v('Se quitará', 'Se quitó')} del menú la opción de fábrica "${c.label}": la ` +
            'sustituyen las opciones de tus procesos.'
        : `La opción "${c.label}" del menú actual no viene de ningún proceso ni de las opciones base.`,
    )
  }

  // Un proceso puede quedar sin frases si su única diferencia era la posición y
  // la frase del menú ya la cubrió; entonces deja de ser algo que contar.
  const grouped = processes.filter((d) => d.sentences.length > 0)

  return {
    greetingChanged,
    menuReordered,
    consentChanged,
    processes: grouped,
    categoriesAdded,
    categoriesRemoved,
    unaccountedLiveOptions,
    unaccountedLiveOptionKeys,
    summary,
    // Una opción viva sin dueño NO es un delta vacío. Es justo lo que un
    // `Actualizar` perdería, así que decir "el agente ya refleja tus procesos"
    // con una de ellas encima sería la mentira más cara de esta pantalla. La
    // condición es redundante hoy (su frase ya está en `summary`) y se deja
    // escrita a propósito: si alguien mueve esa frase de lugar, la semántica no
    // se cae con ella.
    isEmpty: summary.length === 0 && unaccountedLiveOptions.length === 0,
  }
}
